import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { LinearProgress, Skeleton } from '@mui/material';
import {
  MAX_MEMBER_NAME_QUERIES,
  buildSuggestedQueries,
} from '@realadvisor/constants/source-match';
import { type DeepPartial } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import type { OrganisationSettingsFragment } from '@graphql/__generated__/graphql';
import type { RaAddressType } from '@modules/_shared/components/form/RaAddressInput';
import {
  type FormDefinitionType,
  type NonInternalProps,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import {
  MultiEmailsField,
  type MultiEmailsFieldFormData,
} from '@modules/_shared/components/MultiEmailsField';
import {
  MultiPhoneNumbersField,
  type MultiPhoneNumbersFieldFormData,
} from '@modules/_shared/components/MultiPhoneNumbersField';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useTenantIntegrations } from '@modules/_shared/hooks/useTenantIntegrations';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { isDeepEqual } from '@modules/_shared/utils/objects';
import { formatPhoneNumberE164 } from '@modules/_shared/utils/phone';
import {
  type KnownContact,
  OrganisationAccountFields,
} from '@modules/organisations/components/OrganisationAccountFields';
import {
  getCloseLeadUrl,
  getStripeCustomerUrl,
} from '@modules/organisations/OrganisationMonitoringConnections';
import {
  GET_ORGANISATION_ACCOUNT_FIELDS,
  GET_ORGANISATION_BILLING_SUMMARY,
  GET_ORGANISATION_MONITORING,
  GET_ORGANISATION_SETTINGS,
  UPDATE_ORGANISATION_SETTINGS,
} from '@modules/organisations/organisationsQueries';
import type { SourceMatch } from '@modules/teams/components/MatchSuggestionField';
import { useLocale } from '@shared/hooks/locale';
import { isRATenantId } from '@shared/utils/tenant';

export const ORGANISATION_SETTINGS_FRAGMENT = gql(/* GraphQL */ `
  fragment OrganisationSettings on organisations {
    id
    name
    branding_color
    website
    is_lender
    ...OrganisationAddress
    lat
    lng
    phone_numbers {
      ...MultiPhoneNumbersField
    }
    emails {
      ...MultiEmailsField
    }
  }
`);

type OrganisationSettingsFormData = Pick<
  OrganisationSettingsFragment,
  'name' | 'website' | 'branding_color' | 'is_lender'
> & {
  address: RaAddressType;
} & MultiPhoneNumbersFieldFormData &
  MultiEmailsFieldFormData;

export const OrganisationSettings: React.FC<{
  organisationId: string;
  onSaved?: () => void;
}> = ({ organisationId, onSaved }) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const showAccountFields =
    me?.is_admin === true && isRATenantId(me.tenant_id ?? '');
  const { hasStripeKey, hasCloseKey } = useTenantIntegrations();
  const [displaySuccessMessage, setDisplaySuccessMessage] = useState(false);
  const { data, loading } = useQuery(GET_ORGANISATION_SETTINGS, {
    variables: { id: organisationId },
  });
  const { data: accountData } = useQuery(GET_ORGANISATION_ACCOUNT_FIELDS, {
    variables: { id: organisationId },
    skip: !showAccountFields,
  });

  const [selectedStripe, setSelectedStripe] = useState<SourceMatch[]>([]);
  const [selectedClose, setSelectedClose] = useState<SourceMatch | null>(null);
  const [linkedTenantId, setLinkedTenantId] = useState<string | null>(null);
  const [salesRepUserId, setSalesRepUserId] = useState<string | null>(null);
  const [csmRepUserId, setCsmRepUserId] = useState<string | null>(null);

  // Hydrate the account selections once the admin-only fields are loaded.
  useEffect(() => {
    const organisation = accountData?.organisations_by_pk;
    if (organisation == null) {
      return;
    }
    setSelectedStripe(
      (organisation.stripe_customer_ids ?? []).map(id => ({
        id,
        url: getStripeCustomerUrl(id),
      })),
    );
    setSelectedClose(
      organisation.close_lead_id != null
        ? {
            id: organisation.close_lead_id,
            url: getCloseLeadUrl(organisation.close_lead_id),
          }
        : null,
    );
    setLinkedTenantId(organisation.linked_tenant_id ?? null);
    setSalesRepUserId(organisation.sales_rep_user_id ?? null);
    setCsmRepUserId(organisation.csm_rep_user_id ?? null);
  }, [accountData]);

  const [updateOrganisationSettings] = useMutation(
    UPDATE_ORGANISATION_SETTINGS,
  );

  const addStripeMatch = useCallback((match: SourceMatch) => {
    setSelectedStripe(matches =>
      matches.some(item => item.id === match.id)
        ? matches
        : [...matches, match],
    );
  }, []);

  const defaultValues = useMemo<DeepPartial<OrganisationSettingsFormData>>(
    () => ({
      name: data?.organisations_by_pk?.name ?? '',
      website: data?.organisations_by_pk?.website ?? '',
      branding_color: data?.organisations_by_pk?.branding_color ?? '',
      is_lender: data?.organisations_by_pk?.is_lender ?? false,
      address: {
        street_number: data?.organisations_by_pk?.street_number ?? '',
        route: data?.organisations_by_pk?.street ?? '',
        locality: data?.organisations_by_pk?.locality ?? '',
        postcode: data?.organisations_by_pk?.postcode ?? '',
        state: data?.organisations_by_pk?.state ?? '',
        country_code: data?.organisations_by_pk?.country ?? '',
        lat: data?.organisations_by_pk?.lat ?? null,
        lng: data?.organisations_by_pk?.lng ?? null,
      },
      phone_numbers:
        data?.organisations_by_pk?.phone_numbers.map(
          ({ id, number, verified, primary, type }) => ({
            value: number,
            type,
            phone_number_id: id,
            verified: verified ?? false,
            primary: primary ?? false,
          }),
        ) ?? [],
      emails:
        data?.organisations_by_pk?.emails.map(
          ({ id, email, ...emailData }) => ({
            ...emailData,
            value: email,
            email_id: id,
          }),
        ) ?? [],
    }),
    [data],
  );

  const organisationName = data?.organisations_by_pk?.name ?? '';
  const organisationWebsite = data?.organisations_by_pk?.website ?? '';

  // The org's own emails plus its (active) team members' primary emails, each
  // paired with a human label. These both seed the Stripe/Close search (find a
  // billing account by its exact email), let an exact email match recommend a
  // candidate outright (the case a franchise org name alone can't surface), and
  // render as a reference list so the user can eyeball-validate a suggestion.
  const knownContacts = useMemo<KnownContact[]>(() => {
    const organisation = accountData?.organisations_by_pk;
    if (organisation == null) {
      return [];
    }
    const orgName = organisation.name ?? null;
    const contacts: KnownContact[] = [
      ...(organisation.emails ?? []).map(item => ({
        name: orgName,
        email: item.email,
      })),
      ...(organisation.teams ?? []).flatMap(team =>
        (team.teams_users ?? []).flatMap(teamUser =>
          (teamUser.user?.emails ?? []).map(item => ({
            name: teamUser.user?.full_name ?? null,
            email: item.email,
          })),
        ),
      ),
    ];
    // Dedupe on the normalised email, keeping the first (named) occurrence.
    const seen = new Set<string>();
    return contacts.flatMap(contact => {
      const email = contact.email?.trim().toLowerCase();
      if (email == null || email === '' || seen.has(email)) {
        return [];
      }
      seen.add(email);
      return [{ name: contact.name?.trim() || null, email }];
    });
  }, [accountData]);

  const knownEmails = useMemo(
    () => knownContacts.map(contact => contact.email),
    [knownContacts],
  );

  // Team-member names seed the match search so a solo agent's billing account —
  // registered under their name, not the org's ("Côte et Mer Immobilier" billed
  // as "Hélène Rainard") — is found and recommended. Only for small teams: a
  // large brokerage matches on its org/brand name, and adding every agent name
  // would fan out the search and bulk-recommend personal accounts.
  const memberNames = useMemo(() => {
    const names = (accountData?.organisations_by_pk?.teams ?? []).flatMap(
      team =>
        (team.teams_users ?? []).map(teamUser => teamUser.user?.full_name),
    );
    const unique = Array.from(
      new Set(
        names
          .map(name => name?.trim())
          .filter((name): name is string => name != null && name !== ''),
      ),
    );
    return unique.length <= MAX_MEMBER_NAME_QUERIES ? unique : [];
  }, [accountData]);

  // The account section is fed by a *second* query (account fields + the team
  // emails that drive Stripe/Close matching). Render it only once that data has
  // landed so the suggestion search fires a single time with the *complete*
  // input set (name + website + emails). Mounting earlier ran a name-only search
  // first, which on a cold open settled to "no match" before the (slow, uncached)
  // email lookup arrived — the "works only on reopen" (cache-warm) symptom.
  const accountReady = accountData?.organisations_by_pk != null;

  // The links already persisted on the org — used to badge their cards as "Set"
  // (distinct from a fresh suggestion). Read from the saved data, not the live
  // selection, so it reflects what is actually stored.
  const savedStripeIds =
    accountData?.organisations_by_pk?.stripe_customer_ids ?? undefined;
  const savedCloseId = accountData?.organisations_by_pk?.close_lead_id ?? null;

  const organisationFormDefinition = useMemo<
    FormDefinitionType<OrganisationSettingsFormData>
  >(
    () =>
      ({ t }) =>
        [
          {
            name: 'details',
            label: t('Details'),
            type: 'category-title',
          },
          {
            name: 'name',
            label: t('Name'),
            type: 'text',
            required: true,
            gridProps: { xs: 12, md: 6 },
          },
          {
            name: 'website',
            label: t('Website URL'),
            type: 'text',
            required: false,
            gridProps: { xs: 12, md: 6 },
          },
          {
            name: 'branding_color',
            label: t('Branding color'),
            type: 'color-picker',
            gridProps: { xs: 12, md: 6 },
          },
          ...(showAccountFields
            ? ([
                {
                  name: 'account-fields',
                  type: 'custom',
                  gridProps: { xs: 12, md: 12 },
                  element: accountReady ? (
                    <OrganisationAccountFields
                      organisationId={organisationId}
                      suggestedQueries={buildSuggestedQueries(
                        [organisationName, ...memberNames].filter(Boolean),
                        organisationWebsite,
                        knownEmails,
                      )}
                      knownEmails={knownEmails}
                      knownContacts={knownContacts}
                      savedStripeIds={savedStripeIds}
                      savedCloseId={savedCloseId}
                      hasStripeKey={hasStripeKey}
                      hasCloseKey={hasCloseKey}
                      stripeMatches={selectedStripe}
                      onAddStripe={addStripeMatch}
                      onRemoveStripe={id =>
                        setSelectedStripe(matches =>
                          matches.filter(item => item.id !== id),
                        )
                      }
                      closeMatch={selectedClose}
                      onSelectClose={setSelectedClose}
                      salesRepUserId={salesRepUserId}
                      setSalesRepUserId={setSalesRepUserId}
                      csmRepUserId={csmRepUserId}
                      setCsmRepUserId={setCsmRepUserId}
                      linkedTenantId={linkedTenantId}
                      setLinkedTenantId={setLinkedTenantId}
                    />
                  ) : (
                    <Skeleton variant="rounded" height={220} />
                  ),
                },
              ] as ReturnType<FormDefinitionType<OrganisationSettingsFormData>>)
            : []),
          {
            name: 'address-title',
            label: t('Address'),
            type: 'category-title',
          },
          {
            label: t('Address'),
            type: 'address',
            gridProps: { xs: 12 },
            path: 'address',
            includeGoogleFields: true,
            requiredFields: () => ({
              route: false,
              postcode: false,
              locality: false,
            }),
          },
          {
            name: 'contacts',
            label: t('Contact information'),
            type: 'category-title',
          },
          {
            name: 'phone_numbers',
            type: 'custom',
            element: (
              <MultiPhoneNumbersField
                parentUnicityCheck={{ organisation_id: organisationId }}
              />
            ),
            gridProps: { xs: 12, md: 12 },
          },
          {
            name: 'emails',
            type: 'custom',
            element: (
              <MultiEmailsField
                parentUnicityCheck={{ organisation_id: organisationId }}
              />
            ),
            gridProps: { xs: 12, md: 12 },
          },
        ],
    [
      accountReady,
      addStripeMatch,
      csmRepUserId,
      hasCloseKey,
      hasStripeKey,
      knownEmails,
      knownContacts,
      memberNames,
      linkedTenantId,
      organisationId,
      organisationName,
      organisationWebsite,
      salesRepUserId,
      savedStripeIds,
      savedCloseId,
      selectedClose,
      selectedStripe,
      showAccountFields,
    ],
  );

  const onSubmit = useCallback(
    (formData: NonInternalProps<OrganisationSettingsFormData>) => {
      const { phone_numbers, emails, address, ...organisation } = formData;
      const shouldUpdatePhoneNumbers = !isDeepEqual(
        defaultValues.phone_numbers,
        phone_numbers,
      );
      const shouldUpdateEmails = !isDeepEqual(defaultValues.emails, emails);

      return updateOrganisationSettings({
        // The settings mutation only returns `OrganisationSettings`, so Apollo
        // can auto-update name/branding on the org entity but not the account
        // links (stripe/close/tenant/reps) nor the server-derived billing
        // summary. Refetch the account-fields query so the saved ids update and
        // the just-linked cards flip to the green "Set" state, plus the
        // analytics queries so the top card + billing reflect the new links.
        //
        // `awaitRefetchQueries` is required here: `onCompleted` closes the
        // drawer (which unmounts this component), so without awaiting, the
        // navigation races the not-yet-resolved refetches and the cards keep
        // showing stale data until a manual reload.
        refetchQueries: showAccountFields
          ? [
              GET_ORGANISATION_ACCOUNT_FIELDS,
              GET_ORGANISATION_MONITORING,
              GET_ORGANISATION_BILLING_SUMMARY,
            ]
          : [],
        awaitRefetchQueries: showAccountFields,
        variables: {
          id: organisationId,
          organisation: {
            ...organisation,
            ...(showAccountFields && {
              stripe_customer_ids: selectedStripe.map(match => match.id),
              close_lead_id: selectedClose?.id ?? null,
              linked_tenant_id: linkedTenantId,
              sales_rep_user_id: salesRepUserId,
              csm_rep_user_id: csmRepUserId,
            }),
            street_number: address.street_number ?? null,
            street: address.route ?? null,
            locality: address.locality ?? null,
            postcode: address.postcode ?? null,
            state: address.state ?? null,
            country: address.country_code ?? null,
            lat: address.lat ?? null,
            lng: address.lng ?? null,
          },
          phoneNumbers: phone_numbers.map(
            ({ value, primary, type, verified }) => ({
              number: formatPhoneNumberE164(value) ?? value,
              primary,
              type,
              verified,
              organisation_id: organisationId,
            }),
          ),
          updatePhoneNumbers: shouldUpdatePhoneNumbers,
          updateEmails: shouldUpdateEmails,
          emails: emails.map(({ value, primary, verified }) => ({
            email: value,
            primary,
            verified,
            organisation_id: organisationId,
          })),
        },
        onCompleted: data => {
          if (data.update_organisations_by_pk != null) {
            setDisplaySuccessMessage(true);
            onSaved?.();
          }
        },
      });
    },
    [
      csmRepUserId,
      defaultValues,
      linkedTenantId,
      onSaved,
      organisationId,
      salesRepUserId,
      selectedClose,
      selectedStripe,
      showAccountFields,
      updateOrganisationSettings,
    ],
  );

  if (loading) {
    return (
      <LinearProgress
        sx={{
          position: 'absolute',
          top: 0,
          width: '100%',
          zIndex: 2000,
        }}
      />
    );
  }

  return (
    <RaForm
      formDefinition={organisationFormDefinition}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      allowSubmitNotDirty={showAccountFields}
    >
      {displaySuccessMessage && (
        <RASnackbar
          message={t('Your changes were saved successfully!')}
          onClose={() => setDisplaySuccessMessage(false)}
          severity="success"
        />
      )}
    </RaForm>
  );
};
